import { Flex, Text, Link, Heading, Divider } from '@customerio/pluma-components/react';
import type { SearchResult } from '@/services/auth/types';
import { isEmptyString } from '@/utils/string';
import { CampaignPreview } from './campaign-preview';
import { SegmentPreview } from './segment-preview';
import { TemplatePreview } from './template-preview';
import { ActionPreview } from './action-preview';
import { NewsletterPreview } from './newsletter-preview';
import { CustomerPreview } from './customer-preview';

function PreviewContent({ result }: { result: SearchResult }): React.JSX.Element | null {
  switch (result.kind) {
    case 'campaign':
      return <CampaignPreview result={result} />;
    case 'segment':
      return <SegmentPreview result={result} />;
    case 'template':
      return <TemplatePreview result={result} />;
    case 'action':
      return <ActionPreview result={result} />;
    case 'newsletter':
      return <NewsletterPreview result={result} />;
    case 'customer':
      return <CustomerPreview result={result} />;
    case 'navigation':
      return null;
  }
}

function Description({ result }: { result: SearchResult }) {
  if (result.kind === 'campaign' || result.kind === 'segment') {
    const value = result.other.description;
    return (
      <Text
        size="xs"
        color={isEmptyString(value) ? 'placeholder' : 'subtle'}
        className={!isEmptyString(value) ? 'fs-mask' : undefined}
      >
        {!isEmptyString(value) ? value : '—'}
      </Text>
    );
  }

  if (result.kind === 'action' && !isEmptyString(result.other.campaign_name)) {
    let campaignUrl: string | null = null;
    try {
      campaignUrl = !isEmptyString(result.other.campaign_url) ? new URL(result.other.campaign_url).pathname : null;
    } catch {
      // malformed URL — fall through to plain text
    }

    return (
      <Text size="xs" color="subtle">
        from Campaign{' '}
        {campaignUrl != null ? (
          <Link href={campaignUrl} className="fs-mask">
            {result.other.campaign_name}
          </Link>
        ) : (
          <Text as="span" size="xs" className="fs-mask">
            {result.other.campaign_name}
          </Text>
        )}
      </Text>
    );
  }

  return null;
}

export function QuickLookPanel({ result }: { result: SearchResult | null }) {
  if (result == null || result.kind === 'navigation') {
    return (
      <Flex direction="column" alignItems="center" justifyContent="center" w="full" h="full" p="200">
        <Text size="xs" color="subtle">
          No preview
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="200" w="full" h="full" overflow="auto" p="200">
      <Flex direction="column" gap="050">
        <Heading level="2" className="fs-mask">
          {result.title}
        </Heading>
        <Description result={result} />
      </Flex>

      <Divider />

      <Flex direction="column" gap="100" w="full" alignItems="stretch">
        <PreviewContent result={result} />
      </Flex>
    </Flex>
  );
}
